/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import APIManager from "../../ApiManager/ApiManager";
import {
  FETCH_CPU_DETAILS_REQUEST,
  FETCH_CPU_DETAILS_SUCCESS,
  FETCH_CPU_DETAILS_FAILURE,
} from "./CpuDetailsTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

const CPU_CACHE_KEY = 'cpu-cache';
const CPU_CACHE_TIMESTAMP_KEY = 'cpu-cache-timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const safeParseCache = () => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(CPU_CACHE_KEY);
    const timestamp = sessionStorage.getItem(CPU_CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) return null;

    // Check if cache has expired
    const cacheTime = Number.parseInt(timestamp, 10);
    const currentTime = Date.now();
    if (currentTime - cacheTime > CACHE_DURATION_MS) {
      // Cache expired
      sessionStorage.removeItem(CPU_CACHE_KEY);
      sessionStorage.removeItem(CPU_CACHE_TIMESTAMP_KEY);
      return null;
    }

    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to parse CPU cache', error);
    return null;
  }
};

const persistCache = (cpuData) => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(CPU_CACHE_KEY, JSON.stringify(cpuData));
    sessionStorage.setItem(CPU_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to persist CPU cache', error);
  }
};

export const fetchCpuDetailsRequest = () => {
  return {
    type: FETCH_CPU_DETAILS_REQUEST,
  };
};

export const fetchCpuDetailsSuccess = (cpuData) => {
  return {
    type: FETCH_CPU_DETAILS_SUCCESS,
    payload: cpuData,
  };
};

export const fetchCpuDetailsFailure = (error) => {
  return {
    type: FETCH_CPU_DETAILS_FAILURE,
    payload: error,
  };
};

export const fetchCpuDetails = () => {
  return async function (dispatch) {
    // Check cache first
    const cachedCpuData = safeParseCache();
    if (cachedCpuData && cachedCpuData.length > 0) {
      dispatch(fetchCpuDetailsSuccess(cachedCpuData));
      return cachedCpuData;
    }

    dispatch(fetchCpuDetailsRequest());
    try {
      const data = await APIManager.getCpus();
      persistCache(data);
      dispatch(fetchCpuDetailsSuccess(data));
    } catch (error) {
      dispatch(fetchCpuDetailsFailure(error));
      dispatch(commonError(error, FETCH_CPU_DETAILS_FAILURE));
    }
  };
};

export const commonError=(error,errorType)=>{
  return({
    type:COMMON_ERROR,
    payload:{
      message:error.message,
      errorType:errorType
    }
  })
}
